import Layout, { Content } from 'antd/lib/layout/layout';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { compareVersions, isReleaseSkipped } from '../helpers';
import type { RootState } from '../reducers/reducers';
import type { AppComponentProps, AppRouteComponentMatchParams, Nullable } from '../typescriptHelpers';
import { antGrey, runwayColors, titleStyle } from './styles/globalStyles';
import Text from 'antd/lib/typography/Text';
import Button from 'antd/lib/button/button';
import { Event } from '../utils/analytics';
import { FetchableEntityTypeV2 } from '../entityType';
import { MehOutlined } from '@ant-design/icons';
import Result from 'antd/lib/result';
import { LiveReleaseComponent } from './appHome/LiveReleaseComponent';
import { NextReleaseComponent } from './appHome/NextReleaseComponent';
import { UpcomingReleaseComponent } from './appHome/UpcomingReleaseComponent';
import { PastReleaseComponent } from './appHome/PastReleaseComponent';
import Spin from 'antd/lib/spin';
import { trackAppRouteEvent } from '../actions/trackEvent';
import {
    selectApp,
    selectAppPlatform,
    selectAppStoreIntegration,
    selectAppStoreType,
    selectIsAppsDemoModeEnabled,
    selectWorkflowEnabledSteps,
    selectStabilityIntegration,
    selectWorkflowOverview,
    selectShouldHideNonBlockingSteps,
    selectReleaseStepsBlockingConfigForReleaseType,
    selectOrg,
    selectUser,
    selectAllAppsIdsInOrg,
    selectAllAppGroupNamesInOrg,
    selectDemoApp,
} from '../selectors/selectors';
import { selectDestinationsData, selectShouldRenderWorkflowsOverviewForApp } from '../selectors/summaryViewSelectors';
import * as Storage from '../helpers/localStorageHelper';
import { QuickStartCIProgressBanner } from './ciQuickstart/QuickstartCIProgressBanner';
import { useFirstIncompleteQuickStartCIStep } from './ciQuickstart/hooks/useQuickStartCISteps';
import { MobileSideBarHeader } from './buildDistribution/mobileComponents/MobileSideBarHeader';
import { SkippedReleaseComponent } from './appHome/SkippedReleaseComponent';
import { QualitativeOnboardingTaskList } from '@/components/QualitativeOnboardingTaskList';
import { useGetEntityV2, useIsLoading } from '@/utils/hooks/helpers';
import { globalMessage } from '@/utils/globalMessage';
import { AllLifecycleSteps, AppPlatform, AppStoreType, IntegrationType, LifecycleStep, ReleaseStepType } from '@/types';
import { OrgDestinationsResponse, PageViewType } from '@/types/summaryView';
import { useParams } from 'react-router-dom';
import { useSummaryView } from '@/utils/hooks/useSummaryView';
import { useDestinationSelection } from '@/utils/hooks/useDestinationSelection';
import { OverviewTableHeader, PageViewContext } from './orgSummary/OverviewTableHeader';
import {
    resolveInitialPreferences,
    createFilterApplyTracker,
    getDestinationDisplayText,
} from '@/helpers/summaryViewHelpers';
import { SummaryTable } from './orgSummary/SummaryTable';
import { WorkflowOverview } from './workflowOverview/WorkflowOverview';
import { DestinationSelector } from '@/components/appHome/DestinationSelector';
import { Destination } from '@/types/destinations';
import { useGetAppHomeQuery } from '@/middlewares/defaultApi';
import { FetchableEntityType } from '@/entityType';
import { tryFetchEntity } from '@/actions/entities';

export const AppHomeComponent: React.FC<AppComponentProps> = (props: AppComponentProps) => {
    const { orgId, appId } = props;
    const { orgIdParam, appIdParam } = useParams<AppRouteComponentMatchParams>();
    const workflowId = useSelector((state: RootState) => {
        const app = selectApp(state, appId);
        return app.workflowId;
    });

    const dispatch = useDispatch();

    // Shared org/app data
    const destinationsAndVersions = useSelector((state: RootState) => selectDestinationsData(state, orgId));
    const isLoadingDestinationsAndVersions = useIsLoading(FetchableEntityType.OrgDestinationsAndVersions, orgId);

    React.useEffect(() => {
        if (destinationsAndVersions) return;
        dispatch(tryFetchEntity(FetchableEntityType.OrgDestinationsAndVersions, { orgId }, orgId));
    }, [dispatch, orgId, destinationsAndVersions]);

    // If app exists in appDestinations, it passes all workflows overview checks (enforced by backend)
    const shouldRenderWorkflowsOverview = useSelector((state: RootState) =>
        selectShouldRenderWorkflowsOverviewForApp(state, orgId, appId),
    );

    const canAccessTabularView = destinationsAndVersions?.canAccessTabularView ?? false;

    const availableDestinationsForApp: Destination[] = React.useMemo(() => {
        const appEntry = destinationsAndVersions?.appDestinations?.find((app) => app.appId === appId);
        return (
            appEntry?.destinations?.map((d) => ({
                stepConfigId: d.stepConfigId,
                name: d.name,
                platform: d.platform as AppPlatform,
                appStoreType: d.appStoreType as AppStoreType,
                iconURL: d.iconURL,
            })) ?? []
        );
    }, [destinationsAndVersions, appId]);

    // Onboarding / analytics state
    const [quickStartCIDismissed, setQuickStartCIDismissed] = React.useState(
        Storage.getQuickStartCIDismissedForAppId(props.appId),
    );

    // Track pageView changes for amplitude
    const [trackedPageView, setTrackedPageView] = React.useState<string | null>(null);
    const handlePageViewTracking = React.useCallback((pageView: string) => {
        setTrackedPageView((prev) => (prev === pageView ? prev : pageView));
    }, []);

    React.useEffect(() => {
        if (!shouldRenderWorkflowsOverview) {
            handlePageViewTracking(PageViewType.Default);
        }
    }, [shouldRenderWorkflowsOverview, handlePageViewTracking]);

    React.useEffect(() => {
        if (!trackedPageView) return;
        dispatch(
            trackAppRouteEvent({
                orgId,
                appId,
                eventName: Event.ViewAppHome,
                viewType: trackedPageView,
            }),
        );
    }, [dispatch, orgId, appId, trackedPageView]);

    const platform = useSelector((state: RootState) => selectAppPlatform(state, appId));
    const appStoreType = useSelector((state: RootState) => selectAppStoreType(state, appId));

    const saveDismissedQuickstartCI = (isDismissed: boolean) => {
        setQuickStartCIDismissed(isDismissed);
        Storage.setQuickStartCIDismissedForAppId(props.appId, isDismissed);
    };

    const [firstIncompleteQuickStartCIStepIndex, quickStartCIStepCount] = useFirstIncompleteQuickStartCIStep(
        appId,
        orgId,
    );
    const quickStartCIProgress = ((firstIncompleteQuickStartCIStepIndex + 1) / quickStartCIStepCount) * 100;
    const showQuickStartCI =
        quickStartCIProgress < 100 && firstIncompleteQuickStartCIStepIndex !== 0 && !quickStartCIDismissed;

    // Workflow overview state
    const workflowEnabledSteps = useSelector((state: RootState) => selectWorkflowEnabledSteps(state, appId));
    const shouldHideNonBlockingSteps = useSelector((state: RootState) =>
        selectShouldHideNonBlockingSteps(state, appId),
    );
    const nonBlockingStepsConfigMap = useSelector((state: RootState) =>
        selectReleaseStepsBlockingConfigForReleaseType(state, appId),
    );

    const workflowEnabledLifecycleSteps = AllLifecycleSteps.filter((lifecycleStep) => {
        switch (lifecycleStep) {
            case LifecycleStep.Kickoff:
                return !!workflowEnabledSteps[ReleaseStepType.Kickoff];
            case LifecycleStep.Submission:
                return !!workflowEnabledSteps[ReleaseStepType.Submission];
            case LifecycleStep.Release:
                return !!workflowEnabledSteps[ReleaseStepType.Takeoff];
            default:
                return false;
        }
    });

    // Legacy App Home data (using RTK Query for speed)
    const isViewingAppsInDemoMode = useSelector((state: RootState) => selectIsAppsDemoModeEnabled(state));
    const stabilityIntegration = useSelector((state: RootState) => selectStabilityIntegration(state, appId));
    const appStoreIntegration = useSelector((state: RootState) => selectAppStoreIntegration(state, appId));

    const { data, isFetching, error, refetch } = useGetAppHomeQuery(
        { orgId, appId },
        { skip: !destinationsAndVersions || shouldRenderWorkflowsOverview || isViewingAppsInDemoMode },
    );

    function sortReleasesByVersion<T>(releases: Nullable<T & { version: string }>[]): T[] {
        return (
            releases?.slice()?.sort((releaseA, releaseB) => compareVersions(releaseA.version, releaseB.version)) ?? []
        );
    }
    const demoApp = useSelector((state: RootState) => selectDemoApp(state, appId));
    const appHome = isViewingAppsInDemoMode ? demoApp?.appHome : data?.data;
    const liveRelease = appHome?.liveRelease;
    const nextRelease = appHome?.nextRelease;
    const upcomingReleases = sortReleasesByVersion(appHome?.upcomingReleases);
    const pastReleases = sortReleasesByVersion(appHome?.pastReleases);

    const noReleases =
        !isFetching &&
        !liveRelease &&
        !nextRelease &&
        (!upcomingReleases || upcomingReleases?.length === 0) &&
        (!pastReleases || pastReleases?.length === 0);

    if (isLoadingDestinationsAndVersions) {
        return (
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100%',
                    width: '100%',
                }}
            >
                <Spin size="large" />
                <Text style={{ color: runwayColors.primary, paddingTop: 10 }}>Loading...</Text>
            </div>
        );
    }

    return (
        <MobileSideBarHeader orgId={orgId} appId={appId} title="App Home">
            <Layout
                style={{
                    overflow: 'scroll',
                    padding: 36,
                    minWidth: 980,
                }}
            >
                {/*
            Leaving this here until we can better support app-level demo mode
            <Alert
                type="info"
                icon={null}
                message="Not ready to start onboarding?"
                description={
                    <Flex vertical gap={4}>
                        <Text>
                            Try Runway in demo mode to see how the dashboard will look once setup is complete.
                        </Text>
                        <Text type="secondary">
                            Demo mode is read-only — all actions on the dashboard will be disabled.
                        </Text>
                    </Flex>
                }
                action={<TrackedButton onClick={viewInDemoMode}>🔮 Try it in demo mode 🔮</TrackedButton>}
                style={{
                    borderStyle: 'solid',
                    borderColor: hexStringToRGBAString(runwayColors.primary, 0.8),
                    borderWidth: 1,
                    marginBottom: 28,
                }}
            ></Alert> */}

                {showQuickStartCI && (
                    <QuickStartCIProgressBanner
                        appId={appId}
                        orgId={orgId}
                        onDismiss={() => saveDismissedQuickstartCI(true)}
                    />
                )}
                <QualitativeOnboardingTaskList orgId={props.orgId} appId={props.appId} />
                <Content
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    {shouldRenderWorkflowsOverview ?
                        canAccessTabularView ?
                            <TabularViewContent
                                orgId={orgId}
                                appId={appId}
                                orgIdParam={orgIdParam}
                                appIdParam={appIdParam}
                                workflowId={workflowId}
                                destinationsAndVersions={destinationsAndVersions}
                                stabilityIntegrationId={stabilityIntegration?.id}
                                workflowEnabledSteps={workflowEnabledSteps}
                                workflowEnabledLifecycleSteps={workflowEnabledLifecycleSteps}
                                shouldHideNonBlockingSteps={shouldHideNonBlockingSteps}
                                nonBlockingStepsConfigMap={nonBlockingStepsConfigMap}
                                onPageViewChange={handlePageViewTracking}
                            />
                        :   <WorkflowOverviewContent
                                orgId={orgId}
                                appId={appId}
                                orgIdParam={orgIdParam}
                                appIdParam={appIdParam}
                                workflowId={workflowId}
                                availableDestinations={availableDestinationsForApp}
                                stabilityIntegrationId={stabilityIntegration?.id}
                                workflowEnabledSteps={workflowEnabledSteps}
                                workflowEnabledLifecycleSteps={workflowEnabledLifecycleSteps}
                                shouldHideNonBlockingSteps={shouldHideNonBlockingSteps}
                                nonBlockingStepsConfigMap={nonBlockingStepsConfigMap}
                                onPageViewChange={handlePageViewTracking}
                            />

                    :   <>
                            <Text style={{ ...titleStyle, marginBottom: 24 }}>Overview</Text>
                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    width: '100%',
                                    paddingBottom: 65,
                                    flexGrow: 1,
                                    justifyContent: 'center',
                                }}
                            >
                                {upcomingReleases?.map((upcomingReleaseSummary) => {
                                    return (
                                        <UpcomingReleaseComponent
                                            key={upcomingReleaseSummary.id}
                                            summary={upcomingReleaseSummary}
                                            orgId={orgId}
                                            appId={appId}
                                            appStoreType={appStoreType}
                                            platform={platform}
                                        />
                                    );
                                })}
                                {nextRelease && (
                                    <NextReleaseComponent
                                        key={nextRelease.id}
                                        summary={nextRelease}
                                        orgId={orgId}
                                        appId={appId}
                                        platform={platform}
                                        appStoreType={appStoreType}
                                        stabilityIntegration={stabilityIntegration}
                                        appStoreIntegration={appStoreIntegration}
                                    />
                                )}
                                {liveRelease && (
                                    <LiveReleaseComponent
                                        key={liveRelease.id}
                                        summary={liveRelease}
                                        orgId={orgId}
                                        appId={appId}
                                        platform={platform}
                                        appStoreType={appStoreType}
                                        stabilityIntegration={stabilityIntegration}
                                        appStoreIntegration={appStoreIntegration}
                                    />
                                )}
                                {pastReleases?.map((pastReleaseSummary) => {
                                    return isReleaseSkipped(pastReleaseSummary.status) ?
                                            <SkippedReleaseComponent
                                                key={pastReleaseSummary.id}
                                                summary={pastReleaseSummary}
                                                orgId={orgId}
                                                appId={appId}
                                            />
                                        :   <PastReleaseComponent
                                                key={pastReleaseSummary.id}
                                                summary={pastReleaseSummary}
                                                orgId={orgId}
                                                appId={appId}
                                                platform={platform}
                                                appStoreType={appStoreType}
                                                stabilityIntegration={stabilityIntegration}
                                                appStoreIntegration={appStoreIntegration}
                                            />;
                                })}
                                {noReleases && (
                                    <Text strong type="secondary">
                                        No releases
                                    </Text>
                                )}
                                {isFetching && <Spin />}
                                {error && (
                                    <Result
                                        icon={<MehOutlined style={{ color: antGrey.grey5 }} />}
                                        subTitle="Error fetching app overview"
                                        extra={<Button onClick={refetch}>Try again</Button>}
                                    />
                                )}
                            </div>
                        </>
                    }
                </Content>
            </Layout>
        </MobileSideBarHeader>
    );
};

interface TabularViewContentProps {
    orgId: string;
    appId: string;
    orgIdParam: string;
    appIdParam: string;
    workflowId: string;
    destinationsAndVersions: OrgDestinationsResponse;
    stabilityIntegrationId?: IntegrationType;
    workflowEnabledSteps: ReturnType<typeof selectWorkflowEnabledSteps>;
    workflowEnabledLifecycleSteps: LifecycleStep[];
    shouldHideNonBlockingSteps: boolean;
    nonBlockingStepsConfigMap: ReturnType<typeof selectReleaseStepsBlockingConfigForReleaseType>;
    onPageViewChange: (pageView: string) => void;
}

const TabularViewContent: React.FC<TabularViewContentProps> = ({
    orgId,
    appId,
    orgIdParam,
    appIdParam,
    workflowId,
    destinationsAndVersions,
    stabilityIntegrationId,
    workflowEnabledSteps,
    workflowEnabledLifecycleSteps,
    shouldHideNonBlockingSteps,
    nonBlockingStepsConfigMap,
    onPageViewChange,
}) => {
    const summary = useSummaryView({ orgId, appId });
    const {
        preferences,
        availableDestinations,
        availableMetrics,
        isLoadingSummaryView,
        summaryViewResponse,
        initializeWithDefaults,
        fetchSummaryView,
        getStoredPreferences,
    } = summary;

    const selectedDestinationIds = preferences.selectedDestinationIds;

    const org = useSelector((state: RootState) => selectOrg(state, orgId));
    const app = useSelector((state: RootState) => selectApp(state, appId));
    const user = useSelector((state: RootState) => selectUser(state));
    const appsInOrg = useSelector((state: RootState) => selectAllAppsIdsInOrg(state, orgId));
    const appGroupsInOrg = useSelector((state: RootState) => selectAllAppGroupNamesInOrg(state, orgId));

    const handleTrackFilterApply = React.useMemo(
        () => createFilterApplyTracker(org, user, appsInOrg, appGroupsInOrg, preferences, PageViewContext.AppHome, app),
        [org, app, user, appsInOrg, appGroupsInOrg, preferences],
    );

    // Track page view changes
    React.useEffect(() => {
        const { pageView, tabularLayout } = preferences;
        const viewType = pageView === PageViewType.Default ? PageViewType.Default : `${pageView}-${tabularLayout}`;
        onPageViewChange(viewType);
    }, [onPageViewChange, preferences.pageView, preferences.tabularLayout]);

    const workflowDestinationIds = React.useMemo(() => {
        if (!destinationsAndVersions) return [];
        const appEntry = destinationsAndVersions.appDestinations?.find((app) => app.appId === appId);
        return appEntry?.destinations?.map((d) => d.stepConfigId) ?? destinationsAndVersions.allDestinationIds ?? [];
    }, [destinationsAndVersions, appId]);

    const workflowVersions = React.useMemo(() => {
        if (!destinationsAndVersions) return [];
        const appEntry = destinationsAndVersions.appDestinations?.find((app) => app.appId === appId);
        return appEntry?.versions ?? destinationsAndVersions.allVersions ?? [];
    }, [destinationsAndVersions, appId]);

    const defaultVersionsForAppHome = React.useMemo(() => workflowVersions.slice(0, 5), [workflowVersions]);

    React.useEffect(() => {
        if (!destinationsAndVersions) return;
        if (workflowDestinationIds.length === 0 || workflowVersions.length === 0) return;

        initializeWithDefaults({
            versions: defaultVersionsForAppHome,
            destinationIds: workflowDestinationIds,
        });
    }, [
        destinationsAndVersions,
        workflowDestinationIds,
        workflowVersions.length,
        defaultVersionsForAppHome,
        initializeWithDefaults,
    ]);

    const [getWorkflowOverview, getWorkflowOverviewIsLoading, getWorkflowOverviewError] = useGetEntityV2(
        FetchableEntityTypeV2.WorkflowOverview,
        workflowId,
    );
    const workflowOverview = useSelector((state: RootState) => selectWorkflowOverview(state, appId));
    const [shouldDisplayOverviewFetchFailed, setShouldDisplayOverviewFetchFailed] = React.useState(false);

    React.useEffect(() => {
        if (getWorkflowOverviewError) {
            globalMessage.error(getWorkflowOverviewError);
            setShouldDisplayOverviewFetchFailed(true);
        }
    }, [getWorkflowOverviewError]);

    React.useEffect(() => {
        if (!workflowId) return;
        if (!destinationsAndVersions) return;
        if (getWorkflowOverviewIsLoading) return;
        if (workflowOverview) return;

        const destinationIds = workflowDestinationIds.length > 0 ? workflowDestinationIds : selectedDestinationIds;
        if (destinationIds.length === 0) return;

        setShouldDisplayOverviewFetchFailed(false);
        getWorkflowOverview({
            orgId,
            workflowId,
            destinationStepIds: destinationIds,
        });
    }, [
        workflowId,
        destinationsAndVersions,
        getWorkflowOverviewIsLoading,
        workflowOverview,
        selectedDestinationIds,
        workflowDestinationIds,
        getWorkflowOverview,
        orgId,
    ]);

    const retryWorkflowOverviewFetch = React.useCallback(() => {
        if (!workflowId) return;
        const destinationIds = workflowDestinationIds.length > 0 ? workflowDestinationIds : selectedDestinationIds;
        if (destinationIds.length === 0) return;

        setShouldDisplayOverviewFetchFailed(false);
        getWorkflowOverview({
            orgId,
            workflowId,
            destinationStepIds: destinationIds,
        });
    }, [getWorkflowOverview, orgId, workflowId, selectedDestinationIds, workflowDestinationIds]);

    const allMetricKeys = React.useMemo(() => availableMetrics.map((m) => m.key), [availableMetrics]);

    const hasInitialFetchedRef = React.useRef(false);
    React.useEffect(() => {
        if (!destinationsAndVersions) return;
        if (isLoadingSummaryView) return;
        if (summaryViewResponse) return;
        if (hasInitialFetchedRef.current) return;

        const resolved = resolveInitialPreferences(
            getStoredPreferences(),
            { versions: workflowVersions, destinationIds: workflowDestinationIds, metricKeys: allMetricKeys },
            { versions: defaultVersionsForAppHome, destinationIds: workflowDestinationIds, metrics: allMetricKeys },
        );

        if (resolved.destinationIds.length === 0 || resolved.versions.length === 0) return;

        hasInitialFetchedRef.current = true;
        fetchSummaryView({
            destinationIds: resolved.destinationIds,
            versions: resolved.versions,
            properties: resolved.metrics,
        });
    }, [
        isLoadingSummaryView,
        summaryViewResponse,
        destinationsAndVersions,
        workflowDestinationIds,
        workflowVersions,
        defaultVersionsForAppHome,
        allMetricKeys,
        getStoredPreferences,
        fetchSummaryView,
    ]);

    const isWorkflowsEnabledAndMultiDestination = availableDestinations.length > 1;
    const runCardDestinationDisplayText = React.useMemo(
        () => getDestinationDisplayText(selectedDestinationIds, availableDestinations),
        [selectedDestinationIds, availableDestinations],
    );

    return (
        <div style={{ height: '100%', width: '100%', display: 'flex', flexDirection: 'column' }}>
            <OverviewTableHeader
                context={PageViewContext.AppHome}
                summary={summary}
                onTrackFilterApply={handleTrackFilterApply}
            />
            <div style={{ flex: 1, overflow: 'auto' }}>
                {preferences.pageView === PageViewType.Tabular ?
                    <SummaryTable summary={summary} />
                :   <WorkflowOverview
                        workflowOverview={workflowOverview}
                        isLoading={!!getWorkflowOverviewIsLoading}
                        shouldDisplayFetchFailed={shouldDisplayOverviewFetchFailed}
                        onRetry={retryWorkflowOverviewFetch}
                        orgId={orgId}
                        appId={appId}
                        orgIdParam={orgIdParam}
                        appIdParam={appIdParam}
                        selectedDestinationIds={selectedDestinationIds}
                        resolvedDestinations={availableDestinations}
                        runCardDestinationDisplayText={runCardDestinationDisplayText}
                        isWorkflowsEnabledAndMultiDestination={isWorkflowsEnabledAndMultiDestination}
                        stabilityIntegrationId={stabilityIntegrationId}
                        workflowEnabledSteps={workflowEnabledSteps}
                        workflowEnabledLifecycleSteps={workflowEnabledLifecycleSteps}
                        shouldHideNonBlockingSteps={shouldHideNonBlockingSteps}
                        nonBlockingStepsConfigMap={nonBlockingStepsConfigMap}
                    />
                }
            </div>
        </div>
    );
};

interface WorkflowOverviewContentProps {
    orgId: string;
    appId: string;
    orgIdParam: string;
    appIdParam: string;
    workflowId: string;
    availableDestinations: Destination[];
    stabilityIntegrationId?: IntegrationType;
    workflowEnabledSteps: ReturnType<typeof selectWorkflowEnabledSteps>;
    workflowEnabledLifecycleSteps: LifecycleStep[];
    shouldHideNonBlockingSteps: boolean;
    nonBlockingStepsConfigMap: ReturnType<typeof selectReleaseStepsBlockingConfigForReleaseType>;
    onPageViewChange: (pageView: string) => void;
}

const WorkflowOverviewContent: React.FC<WorkflowOverviewContentProps> = ({
    orgId,
    appId,
    orgIdParam,
    appIdParam,
    workflowId,
    availableDestinations,
    stabilityIntegrationId,
    workflowEnabledSteps,
    workflowEnabledLifecycleSteps,
    shouldHideNonBlockingSteps,
    nonBlockingStepsConfigMap,
    onPageViewChange,
}) => {
    const availableDestinationIds = React.useMemo(
        () => availableDestinations.map((d) => d.stepConfigId),
        [availableDestinations],
    );

    const { selectedDestinationIds, setSelectedDestinationIds } = useDestinationSelection({
        appId,
        availableDestinationIds,
    });

    React.useEffect(() => {
        onPageViewChange(PageViewType.Default);
    }, [onPageViewChange]);

    const [getWorkflowOverview, getWorkflowOverviewIsLoading, getWorkflowOverviewError] = useGetEntityV2(
        FetchableEntityTypeV2.WorkflowOverview,
        workflowId,
    );
    const workflowOverview = useSelector((state: RootState) => selectWorkflowOverview(state, appId));
    const [shouldDisplayOverviewFetchFailed, setShouldDisplayOverviewFetchFailed] = React.useState(false);

    React.useEffect(() => {
        if (getWorkflowOverviewError) {
            globalMessage.error(getWorkflowOverviewError);
            setShouldDisplayOverviewFetchFailed(true);
        }
    }, [getWorkflowOverviewError]);

    React.useEffect(() => {
        if (!workflowId) return;
        if (getWorkflowOverviewIsLoading) return;
        if (workflowOverview) return;
        if (selectedDestinationIds.length === 0) return;

        setShouldDisplayOverviewFetchFailed(false);
        getWorkflowOverview({
            orgId,
            workflowId,
            destinationStepIds: selectedDestinationIds,
        });
    }, [
        workflowId,
        getWorkflowOverviewIsLoading,
        workflowOverview,
        selectedDestinationIds,
        getWorkflowOverview,
        orgId,
    ]);

    const retryWorkflowOverviewFetch = React.useCallback(() => {
        if (!workflowId || selectedDestinationIds.length === 0) return;
        setShouldDisplayOverviewFetchFailed(false);
        getWorkflowOverview({
            orgId,
            workflowId,
            destinationStepIds: selectedDestinationIds,
        });
    }, [getWorkflowOverview, orgId, workflowId, selectedDestinationIds]);

    const isWorkflowsEnabledAndMultiDestination = availableDestinations.length > 1;
    const runCardDestinationDisplayText = React.useMemo(
        () => getDestinationDisplayText(selectedDestinationIds, availableDestinations),
        [selectedDestinationIds, availableDestinations],
    );

    return (
        <div style={{ height: '100%', width: '100%', display: 'flex', flexDirection: 'column' }}>
            {isWorkflowsEnabledAndMultiDestination && (
                <div style={{ marginBottom: 24, display: 'flex', minWidth: 480 }}>
                    <DestinationSelector
                        destinations={availableDestinations}
                        selectedDestinationIds={selectedDestinationIds}
                        onSelectionChange={setSelectedDestinationIds}
                        requiresConfirmation
                    />
                </div>
            )}
            <div style={{ flex: 1, overflow: 'auto' }}>
                <WorkflowOverview
                    workflowOverview={workflowOverview}
                    isLoading={!!getWorkflowOverviewIsLoading}
                    shouldDisplayFetchFailed={shouldDisplayOverviewFetchFailed}
                    onRetry={retryWorkflowOverviewFetch}
                    orgId={orgId}
                    appId={appId}
                    orgIdParam={orgIdParam}
                    appIdParam={appIdParam}
                    selectedDestinationIds={selectedDestinationIds}
                    resolvedDestinations={availableDestinations}
                    runCardDestinationDisplayText={runCardDestinationDisplayText}
                    isWorkflowsEnabledAndMultiDestination={isWorkflowsEnabledAndMultiDestination}
                    stabilityIntegrationId={stabilityIntegrationId}
                    workflowEnabledSteps={workflowEnabledSteps}
                    workflowEnabledLifecycleSteps={workflowEnabledLifecycleSteps}
                    shouldHideNonBlockingSteps={shouldHideNonBlockingSteps}
                    nonBlockingStepsConfigMap={nonBlockingStepsConfigMap}
                />
            </div>
        </div>
    );
};
